import { FC, ReactNode, RefCallback, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";

import { BaseProps } from "../../BaseProps";
import { createComponentStyleHook } from "../../tokens";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { LoadingGroup } from "../LoadingGroup";
import { Tooltip } from "../Tooltip";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

export interface ImageProps extends BaseProps {
  /** The image source. */
  readonly src: string;

  /** The image alternative text, required but can be empty string. */
  readonly alt: string;

  /** The optional width of the image. If the value is a number, it's considered pixels. */
  readonly width?: number | string;

  /** The optional height of the image. If the value is a number, it's considered pixels. */
  readonly height?: number | string;

  /** The image tooltip. */
  readonly tooltip?: ReactNode;
}

/**
 * Converts the specified numeric or string value to an appropriate CSS size.
 * @param value The value to get a CSS size from.
 * @returns The CSS size.
 */
const getSize = (value?: number | string): string => {
  if (value === undefined) return "auto";
  if (typeof value === "string") return value;
  return `${value}px`;
};

/**
 * Resolves the pixel dimensions of an image from its `src`.
 * Returns `undefined` for each dimension until the image has loaded.
 * @param src The image source to resolve the dimensions of.
 * @returns The natural dimensions of the image and whether it's still loading.
 */
const useNaturalImageSize = (src: string) => {
  const [size, setSize] = useState<{ width?: number; height?: number }>({});
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setSize({});
    setLoading(true);
    if (!src) {
      setLoading(false);
      return;
    }

    const img = new window.Image();
    img.src = src;
    if (img.complete && img.naturalWidth > 0) {
      setSize({ width: img.naturalWidth, height: img.naturalHeight });
      setLoading(false);
      return;
    }

    let cancelled = false;
    img.onload = () => {
      if (!cancelled) {
        setSize({ width: img.naturalWidth, height: img.naturalHeight });
        setLoading(false);
      }
    };
    img.onerror = () => {
      if (!cancelled) setLoading(false);
    };

    return () => {
      cancelled = true;
    };
  }, [src]);

  return { ...size, loading };
};

const useStyles = createComponentStyleHook<ImageProps>({ legacyStyles, tokenStyles, componentTokens: "image" });
export const Image: FC<ImageProps> = (props) => {
  const { src, tooltip, width, height, alt } = props;
  const { mode, styles, StyleModeBoundary } = useStyles(props);
  const { width: imageSizeWidth, height: imageSizeHeight, loading } = useNaturalImageSize(src);

  const htmlProps = getHtmlPropsFromBaseProps(props, {
    className: styles.image,
  });

  const content = (refHandler?: RefCallback<HTMLElement>): ReactNode => {
    const isDecorative = alt === "";
    return (
      <StyleModeBoundary mode={mode}>
        <LoadingGroup loading={loading}>
          <LoadingGroup.Placeholder
            fallback={<Skeleton height={height ? height : imageSizeHeight} width={width ? width : imageSizeWidth} />}
          >
            <div
              {...htmlProps}
              ref={refHandler}
              aria-busy={loading || undefined}
              style={{ maxInlineSize: getSize(width), maxBlockSize: getSize(height) }}
              {...(isDecorative ? { "aria-hidden": true } : undefined)}
            >
              <img
                src={src}
                width={imageSizeWidth}
                height={imageSizeHeight}
                alt={alt}
                style={width !== undefined || height !== undefined ? { width: getSize(width), height: getSize(height) } : undefined}
                {...(isDecorative ? { role: "presentation" } : undefined)}
              />
            </div>
          </LoadingGroup.Placeholder>
        </LoadingGroup>
      </StyleModeBoundary>
    );
  };

  if (tooltip) return <Tooltip content={tooltip}>{content}</Tooltip>;

  return <>{content()}</>;
};
